import React from 'react';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider, MenuGroupHeader } from '@fluentui/react-components';
import { ChevronDownRegular } from '@fluentui/react-icons';
import { importantStyleRef } from './Helpers/searchPageConfig';

interface IPaginatorProps {
    /** Current page (1-based). */
    page: number;
    /** Total number of pages (from the real result total). */
    totalPages: number;
    /** Total number of results, for the "start–end of total" label. */
    total: number;
    /** Page size, used to compute the shown range. */
    pageSize: number;
    /** Navigate to a page. */
    onChange: (target: number) => void;
    /** Change the page size (optional — shows a "Results per page" section when provided). */
    onPageSizeChange?: (size: number) => void;
    /** Page-size choices offered in the dropdown. */
    pageSizeOptions?: number[];
    /** Merchant style for the menu box (from searchPageConfig.paginatorContainer). */
    containerStyle?: React.CSSProperties;
    /** Merchant style for each page button (from searchPageConfig.paginatorCell). */
    cellStyle?: React.CSSProperties;
    /** Storefront background is dark — style the popover to match. */
    isDark?: boolean;
}

// Cap the jump-to-page list so a huge result set doesn't render hundreds of menu items;
// the prev/next chevrons still reach anything beyond the list.
const MAX_LISTED_PAGES = 30;

/**
 * Compact, dropdown-style paginator for the search page. Now that the result total is known,
 * it reports "start–end of total" and lets the shopper jump to any page (and pick a page size)
 * from a single menu — small enough to sit beside the search bar instead of on its own row.
 */
const Paginator: React.FC<IPaginatorProps> = (props) => {
    const { page, totalPages, total, pageSize } = props;

    const goTo = (target: number) => {
        if (target < 1 || target > totalPages || target === page) return;
        props.onChange(target);
    };

    // The dark theme forces `.ac-pagination-menu`/`.ac-pagination-item` colors with
    // `!important`, so merchant overrides must be applied as inline `!important` too.
    const menuRef = props.containerStyle ? importantStyleRef(props.containerStyle) : undefined;
    const cellRef = props.cellStyle ? importantStyleRef(props.cellStyle) : undefined;

    // Navigation is capped at totalPages (the caller clamps this to the API's page limit), so when
    // the real total exceeds what those pages can show, report it as "N+" rather than an exact count
    // the shopper can't page to.
    const navCap = totalPages * pageSize;
    const capped = total > navCap;
    const shownTotal = capped ? navCap : total;
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, shownTotal);
    const listedPages = Math.min(totalPages, MAX_LISTED_PAGES);
    const sizeOptions = props.pageSizeOptions ?? [25, 50, 100];

    const prevDisabled = page <= 1;
    const nextDisabled = page >= totalPages;
    const disabledStyle: React.CSSProperties = { opacity: 0.4, cursor: 'default' };

    return (
        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            <div style={{ display: 'inline-flex' }} className={'ac-pagination-menu'} ref={menuRef}>
                <div
                    ref={cellRef}
                    onClick={() => goTo(page - 1)}
                    className={'ac-pagination-menu ac-pagination-item'}
                    style={prevDisabled ? disabledStyle : undefined}
                    aria-disabled={prevDisabled}
                    title='Previous page'
                >&laquo;</div>
                <Menu positioning={'below'}>
                    <MenuTrigger disableButtonEnhancement>
                        <div
                            ref={cellRef}
                            className={'ac-pagination-menu ac-pagination-item'}
                            style={{ gap: 6, alignItems: 'center', cursor: 'pointer' }}
                            title='Go to page'
                        >
                            {total > 0 ? `${start}–${end} of ${shownTotal}${capped ? '+' : ''}` : 'No results'}
                            <ChevronDownRegular style={{ fontSize: 14 }} />
                        </div>
                    </MenuTrigger>
                    <MenuPopover className={props.isDark ? 'sf-menu-popover-dark' : undefined}>
                        <MenuList>
                            <MenuGroupHeader>Page {page} of {totalPages}{capped ? '+' : ''}</MenuGroupHeader>
                            {Array.from({ length: listedPages }, (_, i) => i + 1).map((i) => (
                                <MenuItem
                                    key={'paginator-' + i}
                                    disabled={i === page}
                                    onClick={() => goTo(i)}
                                >Page {i}</MenuItem>
                            ))}
                            {props.onPageSizeChange && (
                                <>
                                    <MenuDivider />
                                    <MenuGroupHeader>Results per page</MenuGroupHeader>
                                    {sizeOptions.map((s) => (
                                        <MenuItem
                                            key={'pagesize-' + s}
                                            disabled={s === pageSize}
                                            onClick={() => props.onPageSizeChange?.(s)}
                                        >{s}</MenuItem>
                                    ))}
                                </>
                            )}
                        </MenuList>
                    </MenuPopover>
                </Menu>
                <div
                    ref={cellRef}
                    onClick={() => goTo(page + 1)}
                    className={'ac-pagination-menu ac-pagination-item'}
                    style={nextDisabled ? disabledStyle : undefined}
                    aria-disabled={nextDisabled}
                    title='Next page'
                >&raquo;</div>
            </div>
        </div>
    );
}

export default Paginator;
